import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import config from "../../../config";
import StatsCard from "../../common/New/StatsCard";

const COLORS = {
  text: "#162238",
  muted: "#718096",
  grid: "#E9EDF3",
  blue: "#0B84F3",
  track: "#E7EBF0",
};

const BEHAVIORS = [
  { key: "phone", label: "Phone use", color: "#F34D59" },
  { key: "drowsiness", label: "Drowsiness", color: "#F8B94C" },
  { key: "distraction", label: "Distraction", color: "#0B84F3" },
  { key: "yawning", label: "Yawning", color: "#8B5CF6" },
  { key: "smoking", label: "Smoking", color: "#64748B" },
  { key: "absence", label: "Driver missing from the seat", color: "#142B55" },
  { key: "seatbelt", label: "No seatbelt", color: "#32C77F" },
];

const BEHAVIOR_BY_KEY = Object.fromEntries(BEHAVIORS.map((item) => [item.key, item]));

function rangeBounds(selectedDateRange, customDateRange) {
  const start = new Date();
  const end = new Date();
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  if (selectedDateRange === "custom" && customDateRange?.startDate) {
    const customStart = new Date(customDateRange.startDate);
    const customEnd = customDateRange.endDate ? new Date(customDateRange.endDate) : end;
    customStart.setHours(0, 0, 0, 0);
    customEnd.setHours(23, 59, 59, 999);
    return { from: customStart, to: customEnd };
  }

  if (selectedDateRange === "yesterday") {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
  } else if (selectedDateRange === "last 7 days") {
    start.setDate(start.getDate() - 6);
  } else if (selectedDateRange === "last 30 days" || selectedDateRange === "last_30_days") {
    start.setDate(start.getDate() - 29);
  }

  return { from: start, to: end };
}

function behaviorList(event) {
  const value = event?.behaviors;
  if (Array.isArray(value)) return value.filter((item) => BEHAVIOR_BY_KEY[item]);
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter((item) => BEHAVIOR_BY_KEY[item]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function behaviorLabel(name) {
  return BEHAVIOR_BY_KEY[name]?.label || name;
}

function driverLabel(event) {
  const driver = event?.driver;
  if (!driver) return "Unassigned";
  return `${driver.first_name || ""} ${driver.last_name || ""}`.trim() || "Unassigned";
}

function vehicleText(event) {
  const number = event?.vehicle?.VehicleNumber;
  const name = event?.vehicle?.name;
  if (number && name && number !== name) return `${number} · ${name}`;
  return number || name || "Unlinked";
}

function routeText(event) {
  return event?.vehicle?.route?.name || "No route";
}

function Panel({ title, children }) {
  return (
    <Paper elevation={0} sx={{ border: "1px solid #DDE2EA", borderRadius: 1.5, overflow: "hidden", minWidth: 0 }}>
      <Box sx={{ minHeight: 54, px: 2, borderBottom: "1px solid #EEF1F5", display: "flex", alignItems: "center" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{title}</Typography>
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
}

function RankList({ rows, valueKey, color, emptyLabel }) {
  const { t } = useTranslation();
  const max = Math.max(...rows.map((row) => Number(row[valueKey]) || 0), 1);
  if (!rows.length) {
    return (
      <Typography sx={{ py: 6, textAlign: "center", fontSize: 12, color: COLORS.muted }}>
        {t(emptyLabel)}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "grid", gap: 1.6 }}>
      {rows.map((row) => {
        const value = Number(row[valueKey]) || 0;
        return (
          <Box key={row.id} sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 70px", gap: 1, alignItems: "center" }}>
            <Box>
              <DriverLink driver={row} />
              <Typography noWrap sx={{ fontSize: 10, color: COLORS.muted }}>
                {row.vehicleLabel}
              </Typography>
              <Box sx={{ mt: 0.4, height: 7, borderRadius: 5, bgcolor: COLORS.track, overflow: "hidden" }}>
                <Box sx={{ width: `${(value / max) * 100}%`, height: "100%", bgcolor: color }} />
              </Box>
            </Box>
            <Typography sx={{ textAlign: "right", fontSize: 12, fontWeight: 700 }}>{value}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function DriverLink({ driver, withVehicle = false }) {
  const numericId = Number(driver.id);
  const label = withVehicle && driver.vehicleLabel ? `${driver.name} · ${driver.vehicleLabel}` : driver.name;
  if (!Number.isInteger(numericId) || numericId <= 0) {
    return <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>{label}</Typography>;
  }
  return (
    <Link
      to={`${driver.school ? "/school-drivers" : "/driver"}?driverId=${numericId}`}
      style={{ color: COLORS.blue, fontWeight: 700, fontSize: 12, textDecoration: "none" }}
    >
      {label}
    </Link>
  );
}

export default function DriverBehaviorAnalytics({
  school = false,
  selectedDateRange = "today",
  customDateRange = null,
}) {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const { from, to } = rangeBounds(selectedDateRange, customDateRange);
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(`${config.baseURL}/driver-safety`, {
          params: { from: from.toISOString(), to: to.toISOString(), limit: 2000 },
        });
        if (!cancelled) setEvents(response.data?.events || []);
      } catch (requestError) {
        if (!cancelled) {
          setEvents([]);
          setError(requestError?.response?.data?.message || "Could not load the driver behavior report");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedDateRange, customDateRange]);

  const analytics = useMemo(() => {
    const behaviorCounts = new Map(BEHAVIORS.map((item) => [item.key, 0]));
    const drivers = new Map();
    let behaviorEvents = 0;

    events.forEach((event) => {
      const behaviors = behaviorList(event);
      if (!behaviors.length) return;
      behaviorEvents += 1;
      const driverKey = event.driver?.id || "unassigned";
      const current = drivers.get(driverKey) || {
        id: driverKey,
        school,
        name: driverLabel(event),
        events: 0,
        counts: {},
        vehicles: new Set(),
        routes: new Set(),
      };
      current.events += 1;
      current.vehicles.add(vehicleText(event));
      current.routes.add(routeText(event));
      behaviors.forEach((name) => {
        current.counts[name] = (current.counts[name] || 0) + 1;
        behaviorCounts.set(name, (behaviorCounts.get(name) || 0) + 1);
      });
      drivers.set(driverKey, current);
    });

    const rows = [...drivers.values()]
      .map((driver) => ({
        ...driver,
        vehicleLabel: [...driver.vehicles].join(", "),
        routeLabel: [...driver.routes].join(", "),
        total: BEHAVIORS.reduce((sum, item) => sum + (driver.counts[item.key] || 0), 0),
      }))
      .sort((a, b) => b.events - a.events || a.name.localeCompare(b.name));

    const activeBehaviors = BEHAVIORS.filter((item) => behaviorCounts.get(item.key) > 0);
    const pie = activeBehaviors.map((item) => ({
      name: item.label,
      value: behaviorCounts.get(item.key),
      color: item.color,
    }));
    const pieTotal = pie.reduce((sum, item) => sum + item.value, 0);
    const topViolation = [...behaviorCounts.entries()].sort((a, b) => b[1] - a[1])[0];
    const highest = rows[0]?.events ?? 0;
    const lowest = rows[rows.length - 1]?.events ?? 0;
    const worst = highest === lowest ? rows.slice(0, 5) : rows.filter((driver) => driver.events > lowest).slice(0, 5);
    const worstIds = new Set(worst.map((driver) => driver.id));
    const top = [...rows]
      .reverse()
      .filter((driver) => driver.events < highest && !worstIds.has(driver.id))
      .slice(0, 5);

    return {
      behaviorEvents,
      drivers: rows.length,
      topViolation: topViolation && topViolation[1] > 0 ? behaviorLabel(topViolation[0]) : "None",
      pie,
      pieTotal,
      activeBehaviors,
      worst,
      top,
      violations: rows.slice(0, 8).map((driver) => ({ name: driver.name, count: driver.events })),
      rows,
    };
  }, [events, school]);

  return (
    <Box sx={{ color: COLORS.text }}>
      {error ? <Typography color="error" sx={{ mb: 1 }}>{error}</Typography> : null}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" }, gap: 1.5, mb: 1.5 }}>
        <StatsCard title={t("Behavior events")} value={String(analytics.behaviorEvents)} icon={ReportProblemOutlinedIcon} tone="red" loading={loading} />
        <StatsCard title={t("Drivers involved")} value={String(analytics.drivers)} icon={PersonOutlineOutlinedIcon} tone="amber" loading={loading} />
        <StatsCard title={t("Top violation")} value={analytics.topViolation} icon={WarningAmberOutlinedIcon} tone="purple" loading={loading} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "0.9fr 1.1fr 1.1fr" }, gap: 1.5, mb: 1.5 }}>
        <Panel title={t("Behaviors")}>
          <Box sx={{ height: 190, position: "relative" }}>
            <Box sx={{ position: "absolute", inset: 0, zIndex: 0, display: "grid", placeContent: "center", textAlign: "center", pointerEvents: "none" }}>
              <Typography sx={{ fontSize: 24, fontWeight: 800 }}>{analytics.pieTotal}</Typography>
              <Typography sx={{ fontSize: 10, color: COLORS.muted }}>{t("Violations")}</Typography>
            </Box>
            <Box sx={{ position: "relative", zIndex: 1, height: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analytics.pie.length ? analytics.pie : [{ name: t("No data"), value: 1, color: COLORS.track }]}
                    dataKey="value"
                    nameKey="name"
                    innerRadius="62%"
                    outerRadius="88%"
                    stroke="none"
                  >
                    {(analytics.pie.length ? analytics.pie : [{ name: "empty", color: COLORS.track }]).map((item) => (
                      <Cell key={item.name} fill={item.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    wrapperStyle={{ zIndex: 2, outline: "none" }}
                    contentStyle={{
                      background: "#ffffff",
                      border: "1px solid #DDE2EA",
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Box>
          <Box sx={{ display: "grid", gap: 0.7, mt: 1 }}>
            {BEHAVIORS.map((item) => {
              const value = analytics.pie.find((slice) => slice.name === item.label)?.value || 0;
              return (
                <Box key={item.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 9, height: 9, borderRadius: 0.5, bgcolor: item.color }} />
                  <Typography sx={{ flex: 1, fontSize: 11 }}>{t(item.label)}</Typography>
                  <Typography sx={{ fontSize: 11, color: COLORS.muted }}>
                    {value} ({analytics.pieTotal ? ((value / analytics.pieTotal) * 100).toFixed(1) : "0.0"}%)
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Panel>
        <Panel title={t("Worst drivers")}>
          <RankList rows={analytics.worst} valueKey="events" color="#F34D59" emptyLabel="No behavior events in this date range." />
        </Panel>
        <Panel title={t("Top drivers")}>
          <RankList rows={analytics.top} valueKey="events" color="#32C77F" emptyLabel="No safer driver in this date range. Top drivers appear when another driver has fewer events." />
        </Panel>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Panel title={t("Drivers with the most violations")}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={analytics.violations} margin={{ top: 12, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={COLORS.grid} />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="count" name={t("Violations")} fill="#142B55" radius={[2, 2, 0, 0]} barSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Panel title={t("Drivers by vehicle and route")}>
          <TableContainer sx={{ maxHeight: 280 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[t("Driver"), t("Vehicle"), t("Route"), t("Violations")].map((heading) => (
                    <TableCell key={heading} sx={{ fontSize: 12, fontWeight: 700, color: COLORS.muted }}>{heading}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics.rows.map((driver) => (
                  <TableRow key={driver.id}>
                    <TableCell><DriverLink driver={{ ...driver, vehicleLabel: "" }} /></TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{driver.vehicleLabel}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{driver.routeLabel}</TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: 700 }}>{driver.events}</TableCell>
                  </TableRow>
                ))}
                {!loading && analytics.rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ fontSize: 12, color: COLORS.muted }}>{t("No driver behavior events in this date range.")}</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Panel>
      </Box>

      <Panel title={t("Behavior by driver")}>
        <TableContainer sx={{ maxHeight: 360 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: 12, fontWeight: 700, color: COLORS.muted }}>{t("Driver")}</TableCell>
                {analytics.activeBehaviors.map((item) => (
                  <TableCell key={item.key} align="right" sx={{ fontSize: 12, fontWeight: 700, color: item.color }}>
                    {t(item.label)}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: COLORS.muted }}>{t("Total")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {analytics.rows.map((driver) => (
                <TableRow key={driver.id}>
                  <TableCell><DriverLink driver={driver} withVehicle /></TableCell>
                  {analytics.activeBehaviors.map((item) => (
                    <TableCell key={item.key} align="right" sx={{ fontSize: 12, color: item.color, fontWeight: 700 }}>
                      {driver.counts[item.key] || 0}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700 }}>{driver.total}</TableCell>
                </TableRow>
              ))}
              {!loading && analytics.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={analytics.activeBehaviors.length + 2} sx={{ fontSize: 12, color: COLORS.muted }}>
                    {t("No driver behavior events in this date range.")}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
    </Box>
  );
}
